import { sleep } from '@accedo/general-utils';
import { logger } from '@accedo/logger';
import { Nav } from '@accedo/spatial-nav';
import { ScrollProps } from '@accedo/virtual-scroll';
import { stylesheet } from 'astroturf';
import { FunctionalComponent, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import clsx from 'clsx';
import { RequestRejectionError } from '##/services/tenApi';
import { useErrorScreen } from '##/components/Error';
import { sendApiRequest } from '##/services/tenApi/apirequest';
import { ShowsResponse } from '##/services/tenApi/collections/types';
import { nav } from '##/init';
import { useMountedRef, useKeyPress } from '##/utils/hooks';
import { config, focusCache } from '##/services/store/store';
import { Config } from '##/services/store/types';
import { MenuItem } from '##/services/tenApi/system/types';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import { PageNavigation } from '##/utils/PageNavigation';

import ShowIndexBackground from '##/assets/images/Show-Index-Background.svg';

import {
  EXIT_MODAL_NAV_ID,
  SIDE_MENU_NAV_ID,
  onBackPressOnPagesWithSideNav,
  safeAssignFocus,
} from '##/utils';
import { ChildItem, Grid } from '../Grid/Grid';
import { ShelfTitle } from '../Grid/ShelfTitle';
import { SecondaryNav, SecondaryNavItem } from '../SecondaryNav/SecondaryNav';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .page-container {
    display: flex;
  }

  .index-bg-container {
    position: absolute;
    height: 100vh;
    width: 100vw;
    top: 0;
    z-index: -1;
  }
  
  .no-shrink {
    flex-shrink: 0;
  }

  .shelf-title {
    margin-top: pxToVw(47);
    margin-bottom: pxToVw(8);
    font-size: pxToVw(22);
    padding-left: pxToVw(16);
  }

  .page-contents {
    width: pxToVw(1140);
    height: 100vh;
    padding-left: pxToVw(21);
  }

  .loading-screen {
    margin-top: pxToVh(33);
    font-size: pxToVw(22);
  }

`;

const showsGridLazyLoadBoundary = 2;

export const ShowsIndexComponent: FunctionalComponent<{
  gridItems: ChildItem[];
  scrollProps: Partial<ScrollProps>;
  title: string;
  gridNavId: string;
}> = ({ gridItems, title, gridNavId, scrollProps }) => {
  if (!gridItems.length) {
    return <div>No results</div>;
  }

  return (
    <Fragment>
      <ShelfTitle title={title} className={styles.shelfTitle} />
      <Grid
        className={styles.grid}
        items={gridItems}
        gridCols={6}
        id={gridNavId}
        scrollProps={scrollProps}
        scrollEndBoundary={1}
        lazyLoadBoundary={2}
      />
    </Fragment>
  );
};

type ShowsIndexLandingPageProps = {
  initialCategory: string;
};
const secondaryNavId = 'shows-secondary-nav';
const showsGridNavId = 'shows-index-grid';

const getNavItems = (): SecondaryNavItem[] => {
  const configItems = config.$get() as Config;
  const secondaryShowItems = configItems?.app?.menuConfig?.primary?.find(
    (menuItem) => menuItem.slug === 'shows',
  )?.subItems;
  if (secondaryShowItems) {
    return secondaryShowItems.map((item: MenuItem) => ({
      text: item.label,
      key: item.apiEndpoint,
    }));
  }
  return [];
};

export const ShowsIndexLandingPage: FunctionalComponent<ShowsIndexLandingPageProps> = ({
  initialCategory,
}) => {
  const [pageTitle, setPageTitle] = useState('');
  const [gridItems, setGridItems] = useState<ChildItem[]>([]);
  const [scrollProps, setScrollProps] = useState<Partial<ScrollProps>>({
    primary: true,
  });
  const [isLoadingResults, setIsLoadingResults] = useState(true);
  const [showSecondaryNav, setShowSecondaryNav] = useState(true);
  const [currentEndpoint, setCurrentEndpoint] = useState<string>('');
  const [secondaryNavItems, setSecondaryNavItems] = useState<
    SecondaryNavItem[]
  >([]);
  const isMounted = useMountedRef();
  const { showError } = useErrorScreen();

  const mapSearchResponseToGrid = (
    responseItems: ShowsResponse,
  ): ChildItem[] => {
    const fallbackUrl = '/assets/placeholders/Fallback-Poster.jpg';
    return responseItems?.items
      ?.filter((item) => item.numberOfEpisodes > 0 || item.numberOfExtras > 0)
      .map((item, idx) => ({
        fallbackUrl,
        id: `${item.id}-${idx}`,
        imgHeight: 251,
        imgUrl: item.imageUrl || fallbackUrl,
        imgWidth: 166,
        beforeClickHandler: () => {
          focusCache.tvShowLanding.$set({
            showId: item.id,
            genreKey: currentEndpoint,
          });
        },
        route: `/show/${item.id}`,
        showApiEndpoint: item.apiEndpoint,
      }));
  };

  const onNavSelect = (key: string): void => {
    setCurrentEndpoint(key);
    const navObject = secondaryNavItems.find((item) => item.key === key);
    if (navObject) {
      PageNavigation.subPageRoute$.send(navObject.text);
    }
  };

  const getCachedScrollOptions = (
    gridItems: ChildItem[],
  ): { initialScrollIndex?: number; afterInitialScroll?: () => void } => {
    let initialScrollIndex = 0;
    let defaultFocus = '';
    if (!gridItems.length) return {};
    const { showId } = focusCache.tvShowLanding.$get();
    const prevFocusShowIdx = gridItems.findIndex(
      (data, idx) => data.id === `${showId}-${idx}`,
    );
    if (prevFocusShowIdx > -1) {
      const prevRow = Math.floor(prevFocusShowIdx / 6);
      const prevFocusShow = gridItems[prevFocusShowIdx];
      if (prevRow >= showsGridLazyLoadBoundary) {
        initialScrollIndex = prevRow;
        defaultFocus = prevFocusShow.id;
      } else {
        defaultFocus = prevFocusShow.id;
      }
    } else {
      defaultFocus = secondaryNavId;
    }
    focusCache.tvShowLanding.$clear();
    return {
      initialScrollIndex,
      afterInitialScroll: () => nav.assignFocus(defaultFocus),
    };
  };

  const loadCategory = async (categoryEndpoint: string): Promise<void> => {
    if (!categoryEndpoint) return;
    setIsLoadingResults(true);

    try {
      const categoryData = await sendApiRequest<ShowsResponse>(
        categoryEndpoint,
      );

      if (isMounted.current) {
        setIsLoadingResults(false);
        setPageTitle(categoryData.title);
        const gridItems = mapSearchResponseToGrid(categoryData);
        const {
          initialScrollIndex,
          afterInitialScroll,
        } = getCachedScrollOptions(gridItems);
        setScrollProps({
          initialScrollIndex,
          afterInitialScroll,
          primary: true,
        });
        setGridItems(gridItems);
      }
    } catch (err) {
      setIsLoadingResults(false);
      logger.error(err);
      showError(
        (err as RequestRejectionError).internalErrorType || 'NETWORK_ERROR',
      );
    }
  };

  useKeyPress('BACK', () => {
    const { parent } = nav.currentFocusNode || {};

    if (
      parent !== secondaryNavId &&
      parent !== SIDE_MENU_NAV_ID &&
      parent !== EXIT_MODAL_NAV_ID
    ) {
      safeAssignFocus(secondaryNavId);

      return true;
    }

    return onBackPressOnPagesWithSideNav(parent, secondaryNavId);
  });

  useEffect(() => {
    loadCategory(currentEndpoint);
  }, [currentEndpoint]);

  useEffect(() => {
    const navItems = getNavItems();
    const { genreKey } = focusCache.tvShowLanding.$get();
    setSecondaryNavItems(navItems);
    if (navItems.length) {
      const cachedGenreIdx = navItems.findIndex(
        (item) => item.key === genreKey,
      );
      const initialEndpoint =
        navItems.find(
          (item) => item.text.toLowerCase() === initialCategory?.toLowerCase(),
        )?.key || navItems[cachedGenreIdx > -1 ? cachedGenreIdx : 0].key;
      setCurrentEndpoint(initialEndpoint);
    }
    setLoadingIndicatorVisibility?.(false);
  }, []);

  const onSecondaryItemsUpdate = async (): Promise<void> => {
    const navItems = getNavItems();
    const { genreKey } = focusCache.tvShowLanding.$get();
    const cachedGenreIdx = navItems.findIndex((item) => item.key === genreKey);
    if (cachedGenreIdx > -1) {
      await sleep(1);
      nav.assignFocus(`${secondaryNavId}-item-${cachedGenreIdx}`);
    }
  };

  useEffect(() => {
    onSecondaryItemsUpdate();
  }, [secondaryNavItems]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.indexBgContainer}>
        <ShowIndexBackground />
      </div>
      <Nav.Horizontal id="shows-index-page-nav">
        <div className={styles.noShrink}>
          <SecondaryNav
            items={secondaryNavItems}
            title={'SHOWS'}
            navId={secondaryNavId}
            setShowing={setShowSecondaryNav}
            hidden={!showSecondaryNav && !isLoadingResults}
            onSelect={onNavSelect}
            activeKey={currentEndpoint}
          />
        </div>
        <div
          className={clsx(styles.noShrink, styles.pageContents)}
          id="shows-index-page-nav"
        >
          {isLoadingResults ? (
            <div className={styles.loadingScreen}>Loading...</div>
          ) : (
            <ShowsIndexComponent
              gridItems={gridItems}
              scrollProps={scrollProps}
              title={pageTitle}
              gridNavId={showsGridNavId}
            />
          )}
        </div>
      </Nav.Horizontal>
    </div>
  );
};
